import React, { useState, useEffect, useMemo } from 'react'
import { toast } from "react-toastify";
import ProfileSectionCard from './ProfileSectionCard';
import UserService from "services/user.service";
import AuthService from "services/auth.service";
import ProfilePlaceholder from "assets/icons/profile-placeholder.png";
import { FileTextOutlined } from '@ant-design/icons';

const BasicInformation = ({ onProfileRefresh, profile, isReadOnly = false}) => {
  const avatar = profile?.avatar?.styles?.origin?.url || null;
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState(avatar);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAvatarEmpty, setIsAvatarEmpty] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [languages, setLanguages] = useState([""]);
  const [languageOptions, setLanguageOptions] = useState([]);
  
  const isPendingReview = profile?.pending_sections?.identity;

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const languageData = await UserService.getLanguages();
        if (languageData.languages) setLanguageOptions(languageData.languages);
      } catch (error) {
        console.error("Failed to load dropdown options:", error);
      }
    };
    fetchDropdownOptions();
  }, []);

  const parseLanguagesField = (languages) => {
    if (Array.isArray(languages)) return languages;
    if (typeof languages === "string" && languages.trim() !== "") {
      return languages.split(",").map((s) => s.trim()).filter(Boolean);
    } 
    return [];
  };

  const combinedLanguageOptions = useMemo(() => {
    const newLangs = parseLanguagesField(profile?.languages);
    const allOptions = new Set([...languageOptions, ...newLangs]); // Merge newly added langauge in the dropdown options (when admin add a language that is not in the list)
    return Array.from(allOptions).filter(Boolean);
  }, [languageOptions, profile?.languages]);

  const resetForm = () => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      setGender(profile.sex || "-");
      setSelectedFile(null);
      setPreviewAvatarUrl(avatar);
      setIsAvatarEmpty(false);

      const parsed = parseLanguagesField(profile.languages);
      setLanguages(parsed.length ? parsed : [""]);
    }
  };

  useEffect(() => {
    resetForm();
  }, [profile]);

  const handleBeforeEdit = () => {
    if (isPendingReview) {
      toast.warning("You already have an edit pending admin review. Please wait for approval.");
      return false; // Blocks opening edit mode
    }
    resetForm();
    return true;
  };

  // === Handle profile photo changes ===
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file){
      if (!file.type.startsWith("image/")){
        toast.warning("Please select a valid image file (PNG, JPG, JPEG).");
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        toast.warning("File size should not exceed 4MB.");
        return;
      }
      setSelectedFile(file);
      setPreviewAvatarUrl(URL.createObjectURL(file));
      setIsAvatarEmpty(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setIsAvatarEmpty(true);
    setPreviewAvatarUrl(ProfilePlaceholder);
  }

  // === Handle language changes ===
  const hasDuplicateLanguages = (languageList) => {
    const cleaned = languageList.map((l) => l.trim()).filter(Boolean);
    return new Set(cleaned).size !== cleaned.length;
  };

  const handleLanguageChange = (index, value) => {
    const updated = [...languages];
    updated[index] = value;
    setLanguages(updated);

  };

  const handleAddLanguage = () => {
    setLanguages([...languages, ""]);
  };

  const handleDeleteLanguage = (index) => {
    let updated;
    if (languages.length === 1) {
      updated = [""];
    } else {
      updated = languages.filter((_, i) => i !== index);
    }
    setLanguages(updated);
  };

  const validateInput = () => {
    const trimmedLanguages = languages.map((l) => l.trim());
    if (trimmedLanguages.some((l) => l === "")) {
      toast.error("Please select a language for all the fields or remove the empty ones.");
      return false;
    }

    if (hasDuplicateLanguages(languages)) {
      toast.error("Please remove duplicate languages before saving.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateInput()) {
      throw new Error("Validation failed");
    }

    const formattedLanguages = languages.map((l) => l.trim()).join(", ");

    let initialLanguagesStr = "";
    if (Array.isArray(profile?.languages)) {
      initialLanguagesStr = profile.languages.join(", ");
    } else if (typeof profile?.languages === "string") {
      initialLanguagesStr = profile.languages.split(",").map((s) => s.trim()).filter(Boolean).join(", ");
    }

    const avatarChanged = !!selectedFile || isAvatarEmpty;
    const isChanged =
      avatarChanged||
      firstName !== (profile?.first_name || "") ||
      lastName !== (profile?.last_name || "") ||
      gender !== (profile?.sex || "-") ||
      formattedLanguages !== initialLanguagesStr;

    if (!isChanged) {
      resetForm();
      return;
    }

    const formData = new FormData();
    formData.append("request_type", "identity");

    if (selectedFile) {
      formData.append("files[avatar]", selectedFile);
    } else if (isAvatarEmpty) {
      formData.append("attributes[remove_avatar_url]", "1")
    }

    formData.append("attributes[first_name]", firstName);
    formData.append("attributes[last_name]", lastName);
    formData.append("attributes[sex]", gender);
    formData.append("attributes[languages]", formattedLanguages);
   
    try {
      await UserService.submitProfileChangeRequest(formData);
      toast.success("Changes saved and submitted for admin approval!")
      if (onProfileRefresh) {
        await onProfileRefresh();
      }
    } catch (error) {
      if (error.response?.status === 401){
        AuthService.logout();
        window.location.replace("/eb/login");
      } else {
        toast.error(error.response?.data?.errors?.join(',') || "Failed to update basic information.");
      }
      throw error;
    }
  };

  // === Helper variables ===
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + '' + sizes[i];
  };

  const hasPhotoPresent = !!selectedFile || (!!avatar && !isAvatarEmpty);
  
  const displayFileName = selectedFile
    ? selectedFile.name 
    : hasPhotoPresent
      ? avatar.split('/').pop().split('?')[0].replace(/^thumb_/, '') || "Click Here to View Profile Photo"
      : null;

  const displayFileSize = selectedFile
  ? formatFileSize(selectedFile.size)
  : (hasPhotoPresent && profile?.avatar?.file_size)
    ? formatFileSize(profile.avatar.file_size) 
    : "JPG, JPEG, PNG (Max. 4MB)";

  const full_name = profile?.title
  ? `${profile.title} ${profile.name}`
  : profile?.name

  const parsedLanguage = parseLanguagesField(profile?.languages);

	const general_info = [
    { title: 'Photo', isAvatar: true },
		{ title: 'Full Name', value: full_name },
    { title: 'Gender', value: profile?.sex ? profile.sex.charAt(0).toUpperCase() + profile.sex.slice(1) : "-" },
    { title: 'Languages', value: parsedLanguage, isList: true}
	];

	return (
    <ProfileSectionCard
      title="Basic Information"
      showEditButton={!isReadOnly}
      onBeforeEdit={handleBeforeEdit}
      onSave={handleSave}
      onCancel={resetForm}
      >
        {({isEditing}) => (
          <div className="profile-info-group">
            {/* READ ONLY MODE */}
            {!isEditing && (
              <div className="profile-info-group">
                {general_info.map((item, index) => (
                  <div className="profile-info-row mb-2" key={index}>
                    <h6 className="profile-info-label">{item.title}</h6>
                    <div className="profile-info-value">
                      {item.isAvatar ? (
                        <div className= "w-lg-em-36">
                          { avatar ? (
                            <div className="mb-2">
                              <a href={avatar}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="photo-link"
                              >
                                {displayFileName}
                              </a>
                            </div>
                          ) : (
                            <div className="profile-info-value text-muted">No profile photo</div>
                          )}
                          <img
                            src={avatar || ProfilePlaceholder}
                            alt="avatar-profile"
                            className="rounded-circle avatar-preview-img mt-4 shadow-sm"
                          />
                        </div>
                      ) : item.isList ? (
                        item.value.length > 0 ? (
                          <ul className="mb-0 pl-3">
                            {item.value.map((language, index) => (
                              <li key={index} className="profile-info-value">{language}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="profile-info-value">-</p>
                        )
                      ) : (
                        <p className="profile-info-value">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* EDIT MODE */}
            {isEditing && (
              <div className="profile-info-group">
                {/* Profile photo */}
                <div className="profile-info-row mb-2">
                  <label className="profile-info-label">Photo</label>
                  <div className="profile-info-value">
                    <div className="file-upload-box mb-3">
                      <div className="d-flex align-items-center">
                        {hasPhotoPresent && (
                          <div className="me-3" style={{ marginRight: '16px' }}>
                            <FileTextOutlined className="text-danger" style={{ fontSize: '24px' }}/>
                        </div>
                        )}
                        <div>
                          <div className="text-dark font-weight-medium">
                            {displayFileName ? displayFileName : "Upload your photo here"}
                          </div>
                          <small className="text-muted">
                            {displayFileSize}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <label className={`mb-0 cursor-pointer ${hasPhotoPresent ? "replace-file-btn" : "browse-file-btn"}`}>
                          {hasPhotoPresent ? "Replace" : "Browse File"}
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            className="d-none"
                            onChange={handleFileChange}
                            onClick={(e) => {e.target.value = null; }}
                          />
                        </label>
                        {hasPhotoPresent && (
                          <button
                            type="button"
                            className="clear-file-btn"
                            style={{ paddingLeft: "15px" }}
                            onClick={handleClearFile}
                            title="Remove photo"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <img
                        src={previewAvatarUrl || ProfilePlaceholder}
                        alt="avatar-preview"
                        className="rounded-circle avatar-preview-img shadow-sm mt-4"
                      />
                    </div>
                  </div>
                </div>

                {/* Full Name */}
                <div className="profile-info-row mb-4">
                  <label className="profile-info-label">Full Name</label>
                  <div className="profile-info-value">
                    <div className="row">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <input
                          type="text"
                          className="form-control py-6 px-4 profile-info-value"
                          placeholder="First Name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control py-6 px-4 profile-info-value"
                          placeholder="Last Name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gender */}
                <div className="profile-info-row mb-4">
                  <label className="profile-info-label">Gender</label>
                  <div className="profile-info-value">
                    <select
                    className="form-select form-control"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    >
                      <option value="">Select Gender</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                  </div>
                </div>

                {/* Languages */}
                <div className="profile-info-row">
                  <label className="profile-info-label">Languages</label>
                  <div className="profile-info-value">
                    <div className="flex-grow-1">
                      {languages.map((language, index) => (
                        <div className="mb-4 input-wrapper" key={index}>
                          <select
                            value={language}
                            onChange={(e) => handleLanguageChange(index, e.target.value)}
                            className="form-select form-control profile-info-value w-100 pr-5"
                            autoFocus={index === languages.length - 1 && languages.length > 1}
                          >
                          <option value="">Select a language</option>
                          {combinedLanguageOptions.map((opt, index) => (
                            <option key={index} value={opt}>{opt}</option>
                          ))}
                        </select>
                          {languages.length > 1 && (
                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() => handleDeleteLanguage(index)}
                              style={{ lineHeight: 1, fontSize: '1.75rem', paddingRight: '15px'}}
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="add-btn"
                        onClick={handleAddLanguage}
                      >
                        + Add Language
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          
          </div>

        )}
     
    </ProfileSectionCard>
	);
};

export default BasicInformation;



